import { Injectable } from '@angular/core';
import { AppAuthClaims, PlanType, TokenFBClaims } from '../models/auth.models';

@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  private _cachedClaims: AppAuthClaims | null = null;

  /**
   * Updates the cached custom claims. Called by FirebaseAuthService.
   * @param claims The new claims to cache, or null to clear the cache.
   */
  updateCachedClaims(claims: TokenFBClaims | null): void {
    if (claims) {
      console.log('AuthorizationService: Updating cached claims.');
      // Perform any necessary validation or mapping here if needed
      this._cachedClaims = claims;
    } else {
      console.log('AuthorizationService: Clearing cached claims.');
      this._cachedClaims = null;
    }
  }

  /**
   * Synchronously returns the currently cached custom claims.
   * Note: This might not reflect the latest claims if the token has expired
   * or claims were updated on the backend since the last fetch.
   * Use getCustomClaims() for potentially fetching the latest claims.
   * @returns The cached AppAuthClaims or null if not cached or user not authenticated.
   */
  getCachedCustomClaims(): AppAuthClaims | null {
    return this._cachedClaims;
  }

  /**
   * Clears the locally cached custom claims.
   * The next call to getCustomClaims() (without forceRefresh: true) will attempt to fetch fresh claims
   * if the token is expired, or return cached claims if the token is still valid but cache was cleared.
   * Call getCustomClaims(true) to guarantee fetching fresh claims.
   */
  invalidateClaimsCache(): void {
    console.log('AuthorizationService: Invalidating custom claims cache.');
    this._cachedClaims = null;
  }

  public isFollowerOrBetaOrPremimPlan(follower = true, beta = true, premium = true): boolean {
    // Add null check for _cachedClaims
    if (!this._cachedClaims || !this._cachedClaims.plan) {
      console.warn('AuthorizationService: Cannot check plan, claims or plan data missing.');
      return false;
    }
    const plan = this._cachedClaims.plan;

    const validPlans = [];
    if (follower) {
      validPlans.push(PlanType.Follower);
    }
    if (beta) {
      validPlans.push(PlanType.Beta);
    }
    if (premium) {
      validPlans.push(PlanType.Premium);
    }

    const hasAccess = validPlans.includes(plan.type);

    if (hasAccess) {
      if (plan.exp === null) {
        return true;
      }
      const expireDate = new Date(plan.exp);
      const currentDate = new Date();
      if (expireDate < currentDate) {
        console.warn('role privilege expired');
        return false;
      }
      return true;
    }
    return false;
  }
  /**
   * Checks if the user's current plan is one of the specified valid plans and has not expired.
   * @param validPlans An array of PlanType enums representing the allowed plans.
   * @returns True if the user has one of the valid plans and it's not expired, false otherwise.
   */
  public hasPlan(validPlans: PlanType[]): boolean {
    // Add null check for _cachedClaims
    if (!this._cachedClaims || !this._cachedClaims.plan) {
      console.warn('AuthorizationService: Cannot check plan, claims or plan data missing.');
      return false;
    }
    const plan = this._cachedClaims.plan;

    const hasAccess = validPlans.includes(plan.type);

    if (hasAccess) {
      // Check for expiration only if the plan is valid
      if (plan.exp === null) {
        // Plan never expires (or expiration is not tracked)
        return true;
      }
      const expireDate = new Date(plan.exp);
      const currentDate = new Date();
      if (expireDate < currentDate) {
        console.warn(`AuthorizationService: Plan ${plan.type} access expired on ${expireDate.toISOString()}.`);
        return false;
      }
      // Plan is valid and not expired
      return true;
    }

    // User's plan is not in the valid list
    return false;
  }
}
